import { useEffect, useRef, useState } from "react";

/**
 * Drives narration audio for the current scene.
 *
 *  - Creates one HTMLAudioElement and reuses it across scenes (important on
 *    iOS Safari, where audio must be "unlocked" by a user gesture once; reusing
 *    the same element keeps it unlocked for subsequent scenes).
 *  - Tracks playback position via requestAnimationFrame for smooth sentence
 *    highlighting, falling back to the audio clock.
 *  - Reports completion exactly once per scene load.
 *
 * Graceful: if a scene has no audio src, it reports "ended" immediately so the
 * story can still advance (text-only scenes remain navigable).
 */
export interface SceneAudioState {
  isPlaying: boolean;
  currentMs: number;
  /** True once narration for the current src has finished. */
  ended: boolean;
  /** Whether narration is muted (persists across scenes). */
  muted: boolean;
  toggle: () => void;
  play: () => void;
  pause: () => void;
  /** Toggle mute on the shared audio element. */
  toggleMute: () => void;
  /** Replay the current scene's narration from the start. */
  restart: () => void;
  /**
   * The single shared element, exposed so the voice-breath analyser
   * (sound.attachVoice) can tap the narration it already plays.
   */
  element: HTMLAudioElement | null;
}

export function useSceneAudio(
  src: string | undefined,
  opts: { autoPlay: boolean; onEnded?: () => void } = { autoPlay: true }
): SceneAudioState {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const endedFiredRef = useRef(false);
  const onEndedRef = useRef(opts.onEnded);
  onEndedRef.current = opts.onEnded;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);

  // Lazily create the single shared audio element.
  if (audioRef.current === null && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "auto";
  }

  // (Re)load when the src changes.
  useEffect(() => {
    const audio = audioRef.current;
    endedFiredRef.current = false;
    setEnded(false);
    setCurrentMs(0);

    if (!audio || !src) {
      // No audio: treat as instantly complete so navigation still works.
      setIsPlaying(false);
      if (!endedFiredRef.current) {
        endedFiredRef.current = true;
        setEnded(true);
        onEndedRef.current?.();
      }
      return;
    }

    audio.src = src;
    audio.currentTime = 0;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentMs((audio.duration || 0) * 1000);
      if (!endedFiredRef.current) {
        endedFiredRef.current = true;
        setEnded(true);
        onEndedRef.current?.();
      }
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    if (opts.autoPlay) {
      // play() may reject on iOS if not user-initiated; that's fine — the
      // parent can tap the play button to start.
      audio.play().catch(() => setIsPlaying(false));
    }

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Smoothly track position while playing.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const tick = () => {
      setCurrentMs(audio.currentTime * 1000);
      rafRef.current = requestAnimationFrame(tick);
    };

    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick);
    }
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying]);

  const play = () => {
    audioRef.current?.play().catch(() => setIsPlaying(false));
  };
  const pause = () => audioRef.current?.pause();
  const toggle = () => (isPlaying ? pause() : play());

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  };

  /** Replay the current scene's narration from the top (no-op when text-only). */
  const restart = () => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    endedFiredRef.current = false;
    setEnded(false);
    setCurrentMs(0);
    audio.currentTime = 0;
    audio.play().catch(() => setIsPlaying(false));
  };

  return {
    isPlaying,
    currentMs,
    ended,
    muted,
    toggle,
    play,
    pause,
    toggleMute,
    restart,
    element: audioRef.current,
  };
}
